<template>
  <div class="student-dashboard pa-10">
    <!-- ── Header ── -->
    <div class="d-flex align-center justify-space-between mb-6 flex-wrap gap-3">
      <div>
        <h1 class="dash-title">Student Dashboard</h1>
        <p class="dash-subtitle">Track Your OJT Progress &amp; Requirements</p>
      </div>
      <div class="date-badge">{{ currentDate }}</div>
    </div>

    <!-- ── Stat Cards ── -->
    <v-row class="mb-5">
      <v-col cols="6" sm="3" v-for="stat in statCards" :key="stat.label">
        <div class="stat-card">
          <div class="stat-icon-wrap" :style="{ background: stat.iconBg }">
            <v-icon :color="stat.iconColor" size="22">{{ stat.icon }}</v-icon>
          </div>
          <div>
            <div class="stat-label">{{ stat.label }}</div>
            <div class="stat-value">{{ stat.value }}</div>
          </div>
        </div>
      </v-col>
    </v-row>

    <!-- ── Row 2: OJT Hours + Profiling Form ── -->
    <v-row class="mb-5">
      <!-- OJT Hours Progress -->
      <v-col cols="12" md="6">
        <div class="dash-card">
          <div class="card-header">
            <span class="card-title">OJT Hours Progress</span>
            <v-icon color="#10b981" size="20">mdi-clock-outline</v-icon>
          </div>
          <div v-if="loadingHours" class="d-flex justify-center py-4">
            <v-progress-circular indeterminate color="#3b82f6" size="32" />
          </div>
          <template v-else>
            <div class="d-flex justify-space-between align-center mb-1">
              <span class="prog-text"
                >{{ hoursRendered }} / {{ hoursRequired }} hours</span
              >
              <span class="prog-pct">{{ hoursPercent }}%</span>
            </div>
            <v-progress-linear
              :model-value="hoursPercent"
              color="#3b82f6"
              height="8"
              rounded
            />
            <p class="prog-note mt-2">
              {{ hoursRemaining }} hours remaining to complete requirement
            </p>
            <v-row class="mt-3" dense>
              <v-col cols="6">
                <div class="mini-box mini-box--blue">
                  <div class="mini-label">This Week</div>
                  <div class="mini-value">{{ weekHours }} hrs</div>
                </div>
              </v-col>
              <v-col cols="6">
                <div class="mini-box mini-box--green">
                  <div class="mini-label">This Month</div>
                  <div class="mini-value">{{ monthHours }} hrs</div>
                </div>
              </v-col>
            </v-row>

            <!-- Recent Activity -->
            <div v-if="recentLogs.length" class="mt-4">
              <div class="recent-logs-title">Recent Activity</div>
              <div v-for="log in recentLogs" :key="log.date" class="log-item">
                <span class="log-date">{{ formatLogDate(log.date) }}</span>
                <span class="log-hours">{{ log.hours }} hrs</span>
              </div>
            </div>
          </template>
        </div>
      </v-col>

      <!-- Profiling Form Progress -->
      <v-col cols="12" md="6">
        <div class="dash-card">
          <div class="card-header">
            <span class="card-title">Profiling Form Progress</span>
            <v-icon color="#10b981" size="20">mdi-account-outline</v-icon>
          </div>
          <div v-if="loadingProfiling" class="d-flex justify-center py-4">
            <v-progress-circular indeterminate color="#10b981" size="32" />
          </div>
          <template v-else>
            <div class="d-flex justify-space-between align-center mb-1">
              <span class="prog-text">Overall Completion</span>
              <span class="prog-pct">{{ profilingPercent }}%</span>
            </div>
            <v-progress-linear
              :model-value="profilingPercent"
              color="#10b981"
              height="8"
              rounded
              class="mb-4"
            />
            <div class="profiling-list">
              <div
                v-for="section in profilingSections"
                :key="section.label"
                class="profiling-item"
              >
                <div class="d-flex align-center" style="gap: 10px">
                  <v-icon
                    :color="section.complete ? '#10b981' : '#f59e0b'"
                    size="18"
                  >
                    {{ section.complete ? "mdi-check-circle" : "mdi-alert" }}
                  </v-icon>
                  <span class="profiling-label">{{ section.label }}</span>
                </div>
                <span
                  class="profiling-status"
                  :class="
                    section.complete
                      ? 'pstatus--complete'
                      : 'pstatus--incomplete'
                  "
                >
                  {{ section.complete ? "Complete" : "Incomplete" }}
                </span>
              </div>
            </div>
          </template>
        </div>
      </v-col>
    </v-row>

    <!-- ── Row 3: Required Documents + Weekly Reports ── -->
    <v-row>
      <!-- Required Documents -->
      <v-col cols="12" md="6">
        <div class="dash-card">
          <div class="card-header mb-3">
            <span class="card-title">Required Documents</span>
            <v-icon color="#10b981" size="20">mdi-file-document-outline</v-icon>
          </div>

          <div v-if="loadingReqs" class="d-flex justify-center py-4">
            <v-progress-circular indeterminate color="#10b981" size="32" />
          </div>

          <div v-else>
            <div
              v-for="doc in documents"
              :key="doc.name"
              class="list-item"
              :class="docItemClass(doc.status)"
            >
              <div class="d-flex align-center" style="gap: 10px">
                <v-icon :color="docIconColor(doc.status)" size="20">
                  {{ docIcon(doc.status) }}
                </v-icon>
                <div>
                  <div class="item-name">{{ doc.name }}</div>
                  <div class="item-sub" :class="docSubClass(doc.status)">
                    {{ docStatusLabel(doc.status) }}
                  </div>
                </div>
              </div>
              <v-btn
                v-if="doc.status === 'not_uploaded'"
                size="x-small"
                color="#10b981"
                class="text-white"
                rounded="pill"
                :to="`/student/my-requirements`"
              >
                Upload
              </v-btn>
            </div>
          </div>
        </div>
      </v-col>

      <!-- Weekly Reports -->
      <v-col cols="12" md="6">
        <div class="dash-card">
          <div class="card-header mb-3">
            <span class="card-title">Weekly Reports</span>
            <v-icon color="#10b981" size="20"
              >mdi-clipboard-text-outline</v-icon
            >
          </div>
          <div v-if="loadingReports" class="d-flex justify-center py-4">
            <v-progress-circular indeterminate color="#f59e0b" size="32" />
          </div>
          <template v-else>
            <div
              v-for="report in weeklyReports"
              :key="report.label"
              class="list-item"
              :class="reportItemClass(report.status)"
            >
              <div class="d-flex align-center" style="gap: 10px">
                <v-icon :color="reportIconColor(report.status)" size="20">
                  {{ reportIcon(report.status) }}
                </v-icon>
                <div>
                  <div class="item-name">{{ report.label }}</div>
                  <div class="item-sub" :class="reportSubClass(report.status)">
                    {{ report.sub }}
                  </div>
                </div>
              </div>
              <v-btn
                v-if="report.status === 'due' || report.status === 'overdue'"
                size="x-small"
                :color="report.status === 'overdue' ? '#ef4444' : '#f59e0b'"
                class="text-white"
                rounded="pill"
                :to="`/student/submissions`"
              >
                Submit
              </v-btn>
            </div>
          </template>
        </div>
      </v-col>
    </v-row>

    <!-- Recent Activity -->
    <v-row class="mb-5">
      <v-col cols="12">
        <div class="dash-card">
          <div class="d-flex justify-space-between align-center mb-5">
            <div>
              <div class="card-title">Recent Activity</div>
              <div class="card-sub">Latest system events</div>
            </div>
            <!-- <router-link to="/student/activity-log" class="activity-view-all-link">View All →</router-link> -->
          </div>
          <div v-if="activityLoading" class="d-flex justify-center py-6">
            <v-progress-circular indeterminate color="#10b981" size="32" />
          </div>
          <div
            v-else-if="!recentActivity.length"
            class="text-center py-6"
            style="color: #9ca3af; font-size: 13px"
          >
            No recent activity
          </div>
          <div v-else>
            <div
              v-for="(item, idx) in recentActivity"
              :key="idx"
              class="activity-item rounded-xl pa-4"
              :class="idx < recentActivity.length - 1 ? 'mb-3' : ''"
              :style="getActivityBadgeStyle(item.action)"
            >
              <div class="d-flex align-start ga-3">
                <div
                  class="activity-icon-box rounded-lg pa-2 flex-shrink-0"
                  :style="{ background: getActivityConfig(item.action).iconBg }"
                >
                  <v-icon :color="getActivityColor(item.action)" size="18">{{
                    getActivityIcon(item.action)
                  }}</v-icon>
                </div>
                <div class="flex-grow-1">
                  <div class="d-flex justify-space-between align-start">
                    <div class="activity-title">
                      {{ item.description || item.action }}
                    </div>
                    <v-chip
                      size="x-small"
                      :color="getActivityColor(item.action)"
                      variant="tonal"
                      class="ml-2 flex-shrink-0"
                      >{{ getActivityConfig(item.action).label }}</v-chip
                    >
                  </div>
                  <div class="activity-desc">
                    By {{ item.performed_by_name || "System" }}
                  </div>
                  <div class="activity-time">
                    {{ formatActivityTime(item.created_at) }}
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </v-col>
    </v-row>
  </div>
</template>

<script>
export default {
  name: "StudentDashboard",

  data() {
    return {
      loadingReqs: false,
      loadingHours: false,
      loadingProfiling: false,
      loadingReports: false,

      hoursRendered: 0,
      hoursRequired: 0,
      weekHours: 0,
      monthHours: 0,

      profilingSections: [],

      documents: [],

      weeklyReports: [],

      recentLogs: [],
      recentActivity: [],
      activityLoading: false,

      skillsScore: 0,
    };
  },

  computed: {
    currentDate() {
      return new Date().toLocaleDateString("en-US", {
        weekday: "long",
        year: "numeric",
        month: "long",
        day: "numeric",
      });
    },

    hoursPercent() {
      if (!this.hoursRequired) return 0;
      return Math.min(
        Math.round((this.hoursRendered / this.hoursRequired) * 100),
        100,
      );
    },

    hoursRemaining() {
      return Math.max(this.hoursRequired - this.hoursRendered, 0);
    },

    profilingPercent() {
      const done = this.profilingSections.filter((s) => s.complete).length;
      return Math.round((done / this.profilingSections.length) * 100);
    },

    docsUploaded() {
      return this.documents.filter((d) => d.status === "validated").length;
    },

    docsTotal() {
      return this.documents.length;
    },

    reportsPending() {
      return this.weeklyReports.filter(
        (r) => r.status === "due" || r.status === "overdue",
      ).length;
    },

    statCards() {
      return [
        {
          label: "Hours Rendered",
          value: `${this.hoursRendered}/${this.hoursRequired}`,
          icon: "mdi-clock-outline",
          iconColor: "#3b82f6",
          iconBg: "#eff6ff",
        },
        {
          label: "Documents Uploaded",
          value: `${this.docsUploaded}/${this.docsTotal}`,
          icon: "mdi-file-upload-outline",
          iconColor: "#10b981",
          iconBg: "#ecfdf5",
        },
        {
          label: "Weekly Reports Pending",
          value: this.reportsPending,
          icon: "mdi-clipboard-text-outline",
          iconColor: "#f59e0b",
          iconBg: "#fffbeb",
        },
        {
          label: "Skills Assessment Score",
          value: `${this.skillsScore}%`,
          icon: "mdi-medal-outline",
          iconColor: "#8b5cf6",
          iconBg: "#f5f3ff",
        },
      ];
    },
  },

  mounted() {
    this.loadRequirements();
    this.loadOJTHours();
    this.loadRecentLogs();
    this.loadProfilingProgress();
    this.loadWeeklyReports();
    this.loadSkillsAssessment();
    this.fetchRecentActivity();
  },

  methods: {
    loadRequirements() {
      this.loadingReqs = true;
      this.axiosCall("/student/my/getMyRequirements", "GET")
        .then((res) => {
          if (res && res.data) {
            this.documents = res.data.map((r) => ({
              name: r.requirement_name,
              status: this.mapDocStatus(r.status),
            }));
          }
        })
        .finally(() => {
          this.loadingReqs = false;
        });
    },

    loadOJTHours() {
      this.loadingHours = true;
      this.axiosCall("/student/my/getOJTHours", "GET")
        .then((res) => {
          if (res && res.data) {
            this.hoursRendered = res.data.hoursRendered ?? 0;
            this.hoursRequired = res.data.hoursRequired ?? 0;
            this.weekHours = res.data.weekHours ?? 0;
            this.monthHours = res.data.monthHours ?? 0;
          }
        })
        .finally(() => {
          this.loadingHours = false;
        });
    },

    loadProfilingProgress() {
      this.loadingProfiling = true;
      this.axiosCall("/student/my/getProfilingProgress", "GET")
        .then((res) => {
          if (res && res.data) {
            const labelMap = {
              personal: "Personal Information",
              "Personal Information": "Personal Information",
              educational: "Educational Background",
              "Educational Background": "Educational Background",
              skills: "Your Skills",
              "Skills & Competencies": "Your Skills",
              career: "Deployment Preference",
              "Career Preferences": "Deployment Preference",
              deployment: "Deployment Preference",
            };
            this.profilingSections = res.data.map((s) => {
              const key = s.section || s.label || s.name || "";
              return {
                label: labelMap[key] || key,
                complete: s.complete ?? s.isComplete ?? s.is_complete ?? false,
              };
            });
          }
        })
        .finally(() => {
          this.loadingProfiling = false;
        });
    },

    loadWeeklyReports() {
      this.loadingReports = true;
      this.axiosCall("/student/my/getWeeklyReports", "GET")
        .then((res) => {
          if (res && res.data) {
            this.weeklyReports = res.data;
          }
        })
        .finally(() => {
          this.loadingReports = false;
        });
    },

    loadRecentLogs() {
      this.axiosCall("/student/my/getRecentAttendanceLogs", "GET").then(
        (res) => {
          if (res && res.data) {
            this.recentLogs = res.data;
          }
        },
      );
    },

    formatLogDate(dateStr) {
      return new Date(dateStr).toLocaleDateString("en-US", {
        weekday: "short",
        month: "short",
        day: "numeric",
      });
    },

    loadSkillsAssessment() {
      this.axiosCall("/student/my/getSkillsAssessment", "GET").then((res) => {
        if (res && res.data) {
          this.skillsScore = res.data.overallScore ?? 0;
        }
      });
    },

    mapDocStatus(status) {
      if (status == null) return "not_uploaded";
      if (status == 1) return "pending";
      if (status == 2) return "validated";
      if (status == 3) return "returned";
      return "not_uploaded";
    },

    // Documents
    docIcon(status) {
      if (status === "validated") return "mdi-check-circle";
      if (status === "pending") return "mdi-clock-outline";
      if (status === "returned") return "mdi-alert-circle";
      return "mdi-alert-triangle";
    },
    docIconColor(status) {
      if (status === "validated") return "#10b981";
      if (status === "pending") return "#f59e0b";
      if (status === "returned") return "#ef4444";
      return "#f97316";
    },
    docItemClass(status) {
      if (status === "validated") return "list-item--green";
      if (status === "pending") return "list-item--yellow";
      if (status === "returned") return "list-item--red";
      return "list-item--orange";
    },
    docSubClass(status) {
      if (status === "validated") return "sub--green";
      if (status === "pending") return "sub--yellow";
      return "sub--red";
    },
    docStatusLabel(status) {
      if (status === "validated") return "Uploaded & Validated";
      if (status === "pending") return "Pending Validation";
      if (status === "returned") return "Returned — Resubmit";
      return "Not Yet Uploaded";
    },

    // Weekly Reports
    reportIcon(status) {
      if (status === "validated") return "mdi-check-circle";
      return "mdi-alert";
    },
    reportIconColor(status) {
      if (status === "validated") return "#10b981";
      if (status === "due") return "#f59e0b";
      return "#ef4444";
    },
    reportItemClass(status) {
      if (status === "validated") return "list-item--green";
      if (status === "due") return "list-item--yellow";
      return "list-item--red";
    },
    reportSubClass(status) {
      if (status === "validated") return "sub--green";
      if (status === "due") return "sub--yellow";
      return "sub--red";
    },

    async fetchRecentActivity() {
      this.activityLoading = true;
      try {
        const res = await this.axiosCall("/system-logs?page=1&limit=5", "GET");
        if (res?.data?.data) this.recentActivity = res.data.data;
      } catch (e) {
        console.error("Failed to load recent activity", e);
      } finally {
        this.activityLoading = false;
      }
    },
    getActivityConfig(action) {
      const map = {
        USER_LOGIN: {
          color: "#10b981",
          iconBg: "#d1fae5",
          icon: "mdi-login",
          label: "Login",
        },
        USER_LOGOUT: {
          color: "#6b7280",
          iconBg: "#f3f4f6",
          icon: "mdi-logout",
          label: "Logout",
        },
        BATCH_CREATED: {
          color: "#8b5cf6",
          iconBg: "#ede9fe",
          icon: "mdi-layers-plus",
          label: "Batch",
        },
        STUDENT_ENDORSED: {
          color: "#3b82f6",
          iconBg: "#dbeafe",
          icon: "mdi-account-arrow-right",
          label: "Endorsed",
        },
        STUDENT_DEPLOYED: {
          color: "#10b981",
          iconBg: "#d1fae5",
          icon: "mdi-briefcase-check-outline",
          label: "Deployed",
        },
        STUDENT_PULLED_OUT: {
          color: "#ef4444",
          iconBg: "#fee2e2",
          icon: "mdi-account-minus-outline",
          label: "Pulled Out",
        },
        STUDENT_REASSIGNED: {
          color: "#f59e0b",
          iconBg: "#fef3c7",
          icon: "mdi-account-switch-outline",
          label: "Reassigned",
        },
        REQUIREMENT_SUBMITTED: {
          color: "#3b82f6",
          iconBg: "#dbeafe",
          icon: "mdi-file-upload-outline",
          label: "Submitted",
        },
        REQUIREMENT_APPROVED: {
          color: "#10b981",
          iconBg: "#d1fae5",
          icon: "mdi-file-check-outline",
          label: "Approved",
        },
        REQUIREMENT_DISAPPROVED: {
          color: "#ef4444",
          iconBg: "#fee2e2",
          icon: "mdi-file-cancel-outline",
          label: "Returned",
        },
        ATTENDANCE_SCANNED: {
          color: "#06b6d4",
          iconBg: "#cffafe",
          icon: "mdi-qrcode-scan",
          label: "Attendance",
        },
      };
      return (
        map[action] || {
          color: "#6b7280",
          iconBg: "#f3f4f6",
          icon: "mdi-information-outline",
          label: action,
        }
      );
    },
    getActivityColor(action) {
      return this.getActivityConfig(action).color;
    },
    getActivityIcon(action) {
      return this.getActivityConfig(action).icon;
    },
    getActivityBadgeStyle(action) {
      const c = this.getActivityConfig(action);
      return { background: c.iconBg, border: `1px solid ${c.color}22` };
    },
    formatActivityTime(dateStr) {
      if (!dateStr) return "";
      const diff = Date.now() - new Date(dateStr).getTime();
      const mins = Math.floor(diff / 60000);
      const hours = Math.floor(diff / 3600000);
      const days = Math.floor(diff / 86400000);
      if (mins < 1) return "just now";
      if (mins < 60) return `${mins}m ago`;
      if (hours < 24) return `${hours}h ago`;
      return `${days}d ago`;
    },
  },
};
</script>

<style scoped>
.student-dashboard {
  min-height: 100vh;
}

/* ── Header ── */
.dash-title {
  font-size: 30px;
  font-weight: 800;
  color: #111827;
  letter-spacing: -0.5px;
  margin: 0 0 2px;
  line-height: 1.2;
}
.dash-subtitle {
  font-size: 14px;
  color: #6b7280;
  margin: 0;
}
.date-badge {
  font-size: 13px;
  font-weight: 500;
  color: #374151;
  border: 1px solid #e5e7eb;
  border-radius: 8px;
  padding: 6px 14px;
  background: #fff;
}

/* ── Stat Cards ── */
.stat-card {
  background: #fff;
  border: 1px solid #e5e7eb;
  border-radius: 12px;
  padding: 20px 18px;
  display: flex;
  align-items: center;
  gap: 16px;
  box-shadow: 0 1px 4px rgba(0, 0, 0, 0.06);
  transition: box-shadow 0.2s;
}
.stat-card:hover {
  box-shadow: 0 6px 20px rgba(0, 0, 0, 0.09);
}
.stat-icon-wrap {
  width: 44px;
  height: 44px;
  border-radius: 50%;
  display: flex;
  align-items: center;
  justify-content: center;
  flex-shrink: 0;
}
.stat-label {
  font-size: 13px;
  font-weight: 600;
  color: #6b7280;
  margin-bottom: 4px;
}
.stat-value {
  font-size: 22px;
  font-weight: 700;
  color: #111827;
  letter-spacing: -0.5px;
}

/* ── Dash Card ── */
.dash-card {
  background: #ffffff;
  border: 1px solid #e5e7eb;
  border-radius: 12px;
  box-shadow: 0 1px 6px rgba(0, 0, 0, 0.06);
  padding: 24px;
  height: 100%;
}
.card-header {
  display: flex;
  align-items: center;
  justify-content: space-between;
  margin-bottom: 14px;
}
.card-title {
  font-size: 15px;
  font-weight: 700;
  color: #111827;
}

/* ── OJT Hours ── */
.prog-text {
  font-size: 13px;
  color: #374151;
}
.prog-pct {
  font-size: 14px;
  font-weight: 700;
  color: #374151;
}
.prog-note {
  font-size: 12px;
  color: #9ca3af;
  margin: 0;
}
.mini-box {
  border-radius: 10px;
  padding: 10px 14px;
}
.mini-box--blue {
  background: #eff6ff;
  border: 1px solid #dbeafe;
}
.mini-box--green {
  background: #f0fdf4;
  border: 1px solid #d1fae5;
}
.mini-label {
  font-size: 11px;
  color: #6b7280;
  margin-bottom: 2px;
}
.mini-value {
  font-size: 18px;
  font-weight: 700;
  color: #111827;
}

/* ── Profiling Form ── */
.profiling-list {
  display: flex;
  flex-direction: column;
  gap: 2px;
}
.profiling-item {
  display: flex;
  align-items: center;
  justify-content: space-between;
  padding: 10px 0;
  border-bottom: 1px solid #f3f4f6;
}
.profiling-item:last-child {
  border-bottom: none;
}
.profiling-label {
  font-size: 13px;
  color: #374151;
  font-weight: 500;
}
.profiling-status {
  font-size: 12px;
  font-weight: 600;
}
.pstatus--complete {
  color: #10b981;
}
.pstatus--incomplete {
  color: #f59e0b;
}

/* ── List Items (Documents & Reports) ── */
.list-item {
  display: flex;
  align-items: center;
  justify-content: space-between;
  padding: 12px;
  border-radius: 10px;
  margin-bottom: 8px;
}
.list-item:last-child {
  margin-bottom: 0;
}
.list-item--green {
  background: #f0fdf4;
}
.list-item--yellow {
  background: #fffbeb;
}
.list-item--red {
  background: #fef2f2;
}
.list-item--orange {
  background: #fff7ed;
}

.item-name {
  font-size: 13px;
  font-weight: 600;
  color: #111827;
  margin-bottom: 2px;
}
.item-sub {
  font-size: 12px;
}
.sub--green {
  color: #10b981;
}
.sub--yellow {
  color: #f59e0b;
}
.sub--red {
  color: #ef4444;
}

/* ── Recent Activity ── */
.recent-logs-title {
  font-size: 11.5px;
  font-weight: 600;
  color: #9ca3af;
  text-transform: uppercase;
  letter-spacing: 0.5px;
  margin-bottom: 4px;
}
.log-item {
  display: flex;
  justify-content: space-between;
  align-items: center;
  padding: 8px 0;
  border-bottom: 1px solid #f3f4f6;
}
.log-item:last-child {
  border-bottom: none;
}
.log-date {
  font-size: 12.5px;
  color: #374151;
}
.log-hours {
  font-size: 12.5px;
  font-weight: 700;
  color: #3b82f6;
}

.gap-3 {
  gap: 12px;
}

.activity-view-all-link {
  font-size: 13px;
  font-weight: 600;
  color: #10b981;
  text-decoration: none;
}
.activity-view-all-link:hover {
  text-decoration: underline;
}
.activity-item {
  transition: box-shadow 0.15s;
}
.activity-icon-box {
  display: inline-flex;
}
.activity-title {
  font-size: 13px;
  font-weight: 600;
  color: #111827;
}
.activity-desc {
  font-size: 12px;
  color: #6b7280;
  margin-top: 2px;
}
.activity-time {
  font-size: 11px;
  color: #9ca3af;
  margin-top: 4px;
}
.card-sub {
  font-size: 12.5px;
  color: #9ca3af;
}
</style>
